import { useState } from 'react';
import { formatLengthInput, parseLength, type LengthUnits } from '@peles/shared';

interface LengthInputProps {
  /** The length in feet, or `''` when there is no one length (a mixed selection). */
  value: number | '';
  /** How the length is shown when the field is not being typed in. */
  units: LengthUnits;
  /** Called with feet for every entry that reads as a length, clamped to `min`. */
  onChange: (feet: number) => void;
  min?: number;
  /** Shown when `value` is `''`. */
  placeholder?: string;
  id?: string;
  'aria-label'?: string;
  className?: string;
  'data-testid'?: string;
}

/**
 * A length field that takes feet or inches however they are typed — `6.5`,
 * `6' 6"`, `78"`, `6ft 6in` — whichever way it is shown. What is typed stays
 * as typed while the field has focus (so `6' 6` is not rewritten under the
 * cursor), each entry that reads as a length is applied at once, and leaving
 * the field shows the length again in the chosen units. An entry that does
 * not read as one is marked and changes nothing.
 */
export function LengthInput({
  value,
  units,
  onChange,
  min,
  placeholder,
  id,
  className = 'w-full px-2 py-1 border border-gray-300 rounded text-sm',
  'aria-label': ariaLabel,
  'data-testid': testId,
}: LengthInputProps) {
  const [draft, setDraft] = useState<string | null>(null);
  const invalid = draft !== null && draft.trim() !== '' && parseLength(draft) === null;

  return (
    <input
      id={id}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={draft ?? (value === '' ? '' : formatLengthInput(value, units))}
      placeholder={placeholder}
      onFocus={(e) => setDraft(e.currentTarget.value)}
      onChange={(e) => {
        const text = e.target.value;
        setDraft(text);
        const feet = parseLength(text);
        if (feet !== null) onChange(min === undefined ? feet : Math.max(min, feet));
      }}
      onBlur={() => setDraft(null)}
      onKeyDown={(e) => {
        // Enter settles the entry and Escape drops a half-typed one; either
        // shows the length again in the chosen units.
        if (e.key === 'Enter' || e.key === 'Escape') {
          setDraft(null);
          e.currentTarget.blur();
        }
      }}
      aria-label={ariaLabel}
      aria-invalid={invalid || undefined}
      title={invalid ? `Type a length, such as 6.5, 6' 6" or 78"` : undefined}
      className={`${className}${invalid ? ' border-red-400 text-red-700' : ''}`}
      data-testid={testId}
    />
  );
}
